import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MailCheck, Send } from "lucide-react";
import api from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

export default function CustomerForgotPassword() {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.prefillEmail || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return setError("Please enter your account email");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/customer-forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again in a minute.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-muted/40 p-6">
      <div className="w-full max-w-md rounded-2xl border border-border/70 bg-card p-8 shadow-xl" data-testid="customer-forgot-card">
        <BrandLogo />
        <h1 className="mt-6 font-serif-display text-3xl font-semibold text-secondary">Forgot password</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Enter your account email and we'll send a one-time reset link, valid for 30 minutes.
        </p>

        {sent ? (
          <div className="mt-8 space-y-4" data-testid="customer-forgot-sent">
            <div className="flex items-start gap-2 rounded-xl border border-primary/40 bg-primary/5 p-4 text-sm text-secondary">
              <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <span>
                If an account exists for <span className="font-semibold">{email.trim()}</span>, a reset
                link is on its way. Check your inbox (and spam folder) and follow the link within 30 minutes.
              </span>
            </div>
            <Link to="/account/login" data-testid="customer-forgot-back-login" className="text-sm font-semibold text-primary hover:underline">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="customer-forgot-form">
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Account email
              </label>
              <input
                type="email"
                data-testid="customer-forgot-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full rounded-xl border border-border/70 bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none"
              />
            </div>
            {error && (
              <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive" data-testid="customer-forgot-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              data-testid="customer-forgot-submit"
              disabled={busy}
              className="btn-primary w-full justify-center"
            >
              <Send className="h-4 w-4" /> {busy ? "Sending…" : "Email me a reset link"}
            </button>
            <Link to="/account/login" data-testid="customer-forgot-back-login" className="inline-block text-sm font-semibold text-primary hover:underline">
              Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
